import React, { useState } from 'react';
import Layout from '@/components/Layout';
import { FlaskConical, Lock, Sparkles, ChevronDown, HelpCircle, MapPin, CalendarClock, ClipboardList } from 'lucide-react';

const C = {
  sage: '#E5EBDD', cream: '#FAF6EC', forest: '#1A4331', forestDeep: '#0F2D1F',
  leaf: '#6B9F4A', leafDeep: '#4E7A33', gold: '#C9A85B', terracotta: '#C66B4A', lime: '#B4D330',
  text: '#2A3528', muted: '#5C665C', white: '#FFFFFF',
};
const CARD_SH = '0 6px 16px -2px rgba(26,67,49,0.12)';
const V = {
  h1: { fontFamily: 'Outfit, sans-serif', fontWeight: 700, fontSize: 32, lineHeight: '38px', color: C.forest },
  h3: { fontFamily: 'Outfit, sans-serif', fontWeight: 600, fontSize: 19, lineHeight: '25px', color: C.forest },
  title: { fontFamily: 'Outfit, sans-serif', fontWeight: 600, fontSize: 16, lineHeight: '22px', color: C.forest },
  bodySemi: { fontFamily: 'Manrope, sans-serif', fontWeight: 600, fontSize: 15, lineHeight: '22px', color: C.text },
  body: { fontFamily: 'Manrope, sans-serif', fontWeight: 400, fontSize: 14.5, lineHeight: '23px', color: C.text },
  small: { fontFamily: 'Manrope, sans-serif', fontWeight: 400, fontSize: 13, lineHeight: '19px', color: C.muted },
  label: { fontFamily: 'Manrope, sans-serif', fontWeight: 600, fontSize: 13, lineHeight: '18px', color: C.muted },
};
const SOIL_IMG = 'https://images.pexels.com/photos/31374933/pexels-photo-31374933.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940';

const GUIDE = [
  { icon: HelpCircle, title: 'Why test your soil', body: 'Soil pH and nutrients decide what your plants can actually take up. The right pH unlocks nitrogen, phosphorus and potassium; the wrong one locks them away no matter how much you feed. A quick test shows exactly what your beds need — so you stop guessing and feed with purpose.' },
  { icon: MapPin, title: 'Where to take samples', body: 'Take a small sample from 4–5 spots across each bed, about 10–15 cm deep, then mix them together into one representative sample. Test beds separately if they grow different crops or have been fed differently.' },
  { icon: CalendarClock, title: 'When to test', body: 'Test in early spring before you plant, and again in autumn after harvest. Re-test a bed whenever crops underperform, or after you’ve added a lot of compost, lime or manure.' },
  { icon: ClipboardList, title: 'How to test', body: 'Use an inexpensive home kit or send a sample to a lab. Either way, you’ll soon be able to snap a photo of your results here — we’ll read the pH and NPK for you and turn them into a feeding plan matched to your crops.' },
];

function SoilAdvisor({ user }) {
  const [open, setOpen] = useState(null);
  const [hint, setHint] = useState(false);

  return (
    <Layout user={user}>
      <div className="space-y-6">
        <div>
          <h1 style={V.h1}>Soil &amp; Fertility</h1>
          <p style={V.small} className="mt-1">Your soil, decoded — matched to your crops with a feeding plan</p>
        </div>

        {/* Coming-soon teaser hero */}
        <div className="relative overflow-hidden" style={{ borderRadius: 24, boxShadow: CARD_SH }} data-testid="soil-coming-soon">
          <img src={SOIL_IMG} alt="Hands holding rich soil" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(140deg, rgba(15,45,31,0.82), rgba(15,45,31,0.6) 55%, rgba(26,67,49,0.9))' }} />
          <div className="relative" style={{ padding: 28 }}>
            <span className="inline-flex items-center gap-1.5 rounded-full" style={{ background: 'rgba(180,211,48,0.9)', padding: '5px 12px' }}>
              <Sparkles className="w-3.5 h-3.5" style={{ color: C.forest }} />
              <span style={{ ...V.label, fontSize: 11, color: C.forest }}>Coming soon</span>
            </span>
            <h2 className="mt-4" style={{ ...V.h1, fontSize: 28, lineHeight: '34px', color: C.cream }}>Unlock tailored soil advice</h2>
            <p className="mt-2 max-w-xl" style={{ ...V.body, color: 'rgba(250,246,236,0.9)' }}>
              Log a soil test and we’ll decode your pH, match it to the crops you’re growing, and build a feeding plan — all tailored to your garden. Tailored advice is on its way.
            </p>
            <button onClick={() => setHint(true)} data-testid="soil-log-test-cta"
              className="mt-5 inline-flex items-center gap-2 rounded-full font-semibold" style={{ background: C.lime, color: C.forest, padding: '12px 22px' }}>
              <Lock className="w-4 h-4" /> Log a soil test to unlock
            </button>
            {hint && <p className="mt-3" style={{ ...V.small, color: 'rgba(250,246,236,0.85)' }} data-testid="soil-coming-soon-hint">We’re putting the finishing touches on this — soil test logging and tailored advice land in an upcoming update.</p>}
          </div>
        </div>

        {/* Educational Why / Where / When / How — collapsible */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <span className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: 'rgba(107,159,74,0.14)' }}><FlaskConical className="w-5 h-5" style={{ color: C.leaf }} /></span>
            <h2 style={V.h3}>Soil testing, explained</h2>
          </div>
          <div className="space-y-2.5" data-testid="soil-guide-accordion">
            {GUIDE.map((item, i) => {
              const Icon = item.icon;
              const isOpen = open === i;
              return (
                <div key={i} className="overflow-hidden" style={{ background: C.cream, borderRadius: 18, boxShadow: CARD_SH }}>
                  <button onClick={() => setOpen(isOpen ? null : i)} data-testid={`soil-guide-${i}`}
                    className="w-full flex items-center gap-3 text-left" style={{ padding: 16 }}>
                    <span className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(201,168,91,0.16)' }}><Icon className="w-[18px] h-[18px]" style={{ color: C.gold }} /></span>
                    <span className="flex-1" style={V.title}>{item.title}</span>
                    <ChevronDown className="w-5 h-5 transition-transform flex-shrink-0" style={{ color: C.muted, transform: isOpen ? 'rotate(180deg)' : 'none' }} />
                  </button>
                  {isOpen && <p style={{ ...V.body, padding: '0 16px 16px 16px', color: C.text }}>{item.body}</p>}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default SoilAdvisor;
